import React, { useEffect, useState } from "react";
import { Building2, Upload, Save } from "lucide-react";

const API     = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card    = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls= "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls= "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const EMPTY = {
  name: "", legal_name: "", registration_number: "", tax_id: "",
  industry: "", founded_year: "", website: "", email: "", phone: "",
  address_line1: "", address_line2: "", city: "", state: "", postcode: "", country: "",
  logo_url: "", primary_color: "#00ff90", tagline: "",
  fiscal_year_start: "April", currency: "GBP", timezone: "Europe/London",
};

export default function OrganisationTab() {
  const [form,    setForm]    = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState(null);
  const [logoFile,setLogoFile]= useState(null);
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetch(`${API}/api/admin/organisation`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d && typeof d === "object") setForm(f => ({ ...f, ...d })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      // Upload logo first if selected
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const lr = await fetch(`${API}/api/admin/organisation/logo`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const ld = await lr.json();
        // Upload response is ok()-wrapped: { ok, message, data: { url } }.
        // Read the enveloped signed URL so the preview updates immediately.
        setForm(f => ({ ...f, logo_url: ld.data?.url || ld.url || f.logo_url }));
        setLogoFile(null);
      }
      const res = await fetch(`${API}/api/admin/organisation`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMsg({ type: "success", text: "✓ Organisation details saved." });
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className={`${card} p-5`}><div className="h-48 rounded-lg bg-white/5 animate-pulse" /></div>;

  return (
    <div className="space-y-5">

      {/* Logo + branding */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Building2 size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Branding</span>
        </div>
        <div className="p-5 flex items-start gap-6 flex-wrap">
          {/* Logo preview */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-24 h-24 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden">
              {form.logo_url
                ? <img src={form.logo_url} alt="logo" className="w-full h-full object-contain" />
                : <Building2 size={32} className="text-white/20" />
              }
            </div>
            <label className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors cursor-pointer">
              <Upload size={12} /> Upload Logo
              <input type="file" accept="image/*" className="hidden" onChange={e => setLogoFile(e.target.files[0])} />
            </label>
            {logoFile && <p className="text-xs text-[#00ff90]/50">{logoFile.name}</p>}
          </div>

          <div className="flex-1 grid grid-cols-2 gap-3 min-w-[280px]">
            <div className="col-span-2">
              <label className={labelCls}>Tagline</label>
              <input className={inputCls} value={form.tagline || ""} onChange={e => set("tagline", e.target.value)} placeholder="e.g. Powering great teams" />
            </div>
            <div>
              <label className={labelCls}>Primary Colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color || "#00ff90"} onChange={e => set("primary_color", e.target.value)}
                  className="w-10 h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer" />
                <input className={inputCls} value={form.primary_color || ""} onChange={e => set("primary_color", e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Website</label>
              <input className={inputCls} value={form.website || ""} onChange={e => set("website", e.target.value)} placeholder="https://company.com" />
            </div>
          </div>
        </div>
      </div>

      {/* Core details */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Organisation Details</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>Trading Name</label>
            <input className={inputCls} value={form.name || ""} onChange={e => set("name", e.target.value)} placeholder="KweryAI" />
          </div>
          <div>
            <label className={labelCls}>Legal Name</label>
            <input className={inputCls} value={form.legal_name || ""} onChange={e => set("legal_name", e.target.value)} placeholder="KweryAI Ltd" />
          </div>
          <div>
            <label className={labelCls}>Registration Number</label>
            <input className={inputCls} value={form.registration_number || ""} onChange={e => set("registration_number", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Tax ID / VAT Number</label>
            <input className={inputCls} value={form.tax_id || ""} onChange={e => set("tax_id", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Industry</label>
            <input className={inputCls} value={form.industry || ""} onChange={e => set("industry", e.target.value)} placeholder="Technology" />
          </div>
          <div>
            <label className={labelCls}>Founded Year</label>
            <input type="number" className={inputCls} value={form.founded_year || ""} onChange={e => set("founded_year", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Company Email</label>
            <input type="email" className={inputCls} value={form.email || ""} onChange={e => set("email", e.target.value)} placeholder="info@company.com" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input className={inputCls} value={form.phone || ""} onChange={e => set("phone", e.target.value)} />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Registered Address</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className={labelCls}>Address Line 1</label>
            <input className={inputCls} value={form.address_line1 || ""} onChange={e => set("address_line1", e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Address Line 2</label>
            <input className={inputCls} value={form.address_line2 || ""} onChange={e => set("address_line2", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>City</label>
            <input className={inputCls} value={form.city || ""} onChange={e => set("city", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>State / County</label>
            <input className={inputCls} value={form.state || ""} onChange={e => set("state", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Postcode</label>
            <input className={inputCls} value={form.postcode || ""} onChange={e => set("postcode", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Country</label>
            <input className={inputCls} value={form.country || ""} onChange={e => set("country", e.target.value)} placeholder="United Kingdom" />
          </div>
        </div>
      </div>

      {/* System preferences */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">System Preferences</span>
        </div>
        <div className="p-5 grid grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Currency</label>
            <select className={inputCls} value={form.currency || "GBP"} onChange={e => set("currency", e.target.value)}>
              {["GBP","USD","EUR","INR","AED","SGD","AUD"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Timezone</label>
            <select className={inputCls} value={form.timezone || "Europe/London"} onChange={e => set("timezone", e.target.value)}>
              {["Europe/London","Asia/Kolkata","America/New_York","America/Los_Angeles","Asia/Singapore","Australia/Sydney","Asia/Dubai"].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Fiscal Year Start</label>
            <select className={inputCls} value={form.fiscal_year_start || "April"} onChange={e => set("fiscal_year_start", e.target.value)}>
              {["January","April","July","October"].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>
      </div>

      {msg && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"}`}>
          {msg.text}
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 text-xs px-6 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
          <Save size={13} /> {saving ? "Saving…" : "Save Organisation"}
        </button>
      </div>
    </div>
  );
}